import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, TrendingUp, Users, Target, Shield, Gauge } from 'lucide-react';

const urgencyColors = {
  P0: 'bg-red-500 text-white', P1: 'bg-orange-500 text-white',
  P2: 'bg-yellow-400 text-yellow-900', P3: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  P4: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
};

function ScoreBar({ value, max, color }) {
  const pct = Math.min((value / max) * 100, 100);
  return (
    <div className="h-1.5 bg-[#e3e3e3] dark:bg-white/10 w-full">
      <div className="h-full transition-all duration-300" style={{ width: `${pct}%`, backgroundColor: color }} />
    </div>
  );
}

function EditableCell({ value, onChange, min, max }) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(value);
  useEffect(() => { setLocal(value); }, [value]);

  const commit = () => {
    setEditing(false);
    let v = parseInt(local, 10);
    if (isNaN(v)) v = value;
    if (min !== undefined) v = Math.max(v, min);
    if (max !== undefined) v = Math.min(v, max);
    if (v !== value) onChange(v);
  };

  if (editing) {
    return (
      <input type="number" value={local} min={min} max={max} onChange={e => setLocal(e.target.value)}
        onBlur={commit} onKeyDown={e => e.key === 'Enter' && commit()} autoFocus
        className="w-16 h-8 border border-[#98139C] bg-white dark:bg-white/10 dark:text-white px-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-[#98139C]" />
    );
  }
  return (
    <button onClick={() => setEditing(true)} title="Click to edit"
      className="w-16 h-8 text-sm font-medium text-[#30302F] dark:text-white hover:bg-[#f0dcf0] dark:hover:bg-[#98139C]/10 transition-colors flex items-center justify-center">
      {typeof value === 'number' ? value : '—'}
    </button>
  );
}

export default function Prioritize() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [itemRes, storiesRes] = await Promise.all([
        api.get(`/intake/items/${itemId}`),
        api.get(`/intake/items/${itemId}/prioritized`)
      ]);
      setItem(itemRes.data);
      setStories(storiesRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [itemId]);

  useEffect(() => { loadData(); }, [loadData]);

  const updateRice = async (storyId, field, value) => {
    try {
      const story = stories.find(s => s.id === storyId);
      const updates = { [field]: value };
      // Recalculate RICE score
      const r = field === 'rice_reach' ? value : (story.rice_reach || 0);
      const i = field === 'rice_impact' ? value : (story.rice_impact || 0);
      const c = field === 'rice_confidence' ? value : (story.rice_confidence || 0);
      const e = field === 'rice_effort' ? value : (story.rice_effort || 1);
      updates.rice_score = Math.round((r * i * c) / e * 10) / 10;

      const res = await api.patch(`/intake/stories/${storyId}`, updates);
      setStories(prev => {
        const updated = prev.map(s => s.id === storyId ? { ...s, ...res.data } : s);
        updated.sort((a, b) => (b.rice_score || 0) - (a.rice_score || 0));
        return updated;
      });
    } catch (err) { console.error(err); }
  };

  const updateParentPriority = async (newPriority) => {
    try {
      await api.patch(`/intake/items/${itemId}`, { urgency: newPriority });
      setItem(prev => ({ ...prev, urgency: newPriority }));
    } catch (err) { console.error(err); }
  };

  if (loading) return <div className="p-8 text-[#30302F] dark:text-white">Loading...</div>;
  if (!item) return <div className="p-8">Item not found</div>;

  const maxScore = Math.max(...stories.map(s => s.rice_score || 0), 1);

  return (
    <div className="p-4 lg:p-8 max-w-[1400px] mx-auto space-y-6" data-testid="prioritize-page">
      <button onClick={() => navigate(`/backlog/${itemId}`)} className="flex items-center gap-1.5 text-sm text-[#98139C] hover:underline font-medium" data-testid="back-to-detail">
        <ArrowLeft size={16} /> Back to Item
      </button>

      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#98139C] mb-1">RICE Prioritization</p>
          <h1 className="text-2xl lg:text-3xl font-heading font-semibold text-[#30302F] dark:text-white" style={{ letterSpacing: '-0.4px' }}>{item.title}</h1>
          <p className="text-sm text-[#30302F] dark:text-white mt-2 font-light">
            Click any score to edit. RICE = (Reach x Impact x Confidence) / Effort
          </p>
        </div>
        {/* Parent priority selector */}
        <div className="flex flex-col items-end gap-2 shrink-0" data-testid="priority-selector">
          <span className="text-[11px] uppercase tracking-[0.9px] font-semibold text-[#30302F] dark:text-white">Item Priority</span>
          <div className="flex gap-1">
            {['P0','P1','P2','P3','P4'].map(p => (
              <button key={p} onClick={() => updateParentPriority(p)}
                className={`px-3 py-1.5 text-xs font-bold rounded-sm transition-all ${item.urgency === p ? urgencyColors[p] : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 hover:opacity-80'}`}
                data-testid={`set-priority-${p}`}>
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 flex-wrap text-xs text-[#30302F] dark:text-white bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
        <div className="flex items-center gap-1.5"><Users size={13} /> <span className="font-medium">Reach</span> (1-10)</div>
        <div className="flex items-center gap-1.5"><Target size={13} /> <span className="font-medium">Impact</span> (1-10)</div>
        <div className="flex items-center gap-1.5"><Shield size={13} /> <span className="font-medium">Confidence</span> (1-10)</div>
        <div className="flex items-center gap-1.5"><Gauge size={13} /> <span className="font-medium">Effort</span> (1-10)</div>
      </div>

      {stories.length === 0 ? (
        <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 py-12 text-center text-sm text-[#30302F] dark:text-white">
          No scored stories yet. Go back and run RICE scoring first.
        </div>
      ) : (
        <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 overflow-hidden" data-testid="rice-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#e3e3e3] dark:border-white/10 bg-[#faf8fb] dark:bg-white/5">
                  <th className="text-left p-3 font-semibold text-[11px] uppercase tracking-[0.9px] text-[#30302F] dark:text-white w-8">#</th>
                  <th className="text-left p-3 font-semibold text-[11px] uppercase tracking-[0.9px] text-[#30302F] dark:text-white">Story</th>
                  <th className="text-center p-3 font-semibold text-[11px] uppercase tracking-[0.9px] text-[#30302F] dark:text-white w-20">Reach</th>
                  <th className="text-center p-3 font-semibold text-[11px] uppercase tracking-[0.9px] text-[#30302F] dark:text-white w-20">Impact</th>
                  <th className="text-center p-3 font-semibold text-[11px] uppercase tracking-[0.9px] text-[#30302F] dark:text-white w-20">Conf.</th>
                  <th className="text-center p-3 font-semibold text-[11px] uppercase tracking-[0.9px] text-[#30302F] dark:text-white w-20">Effort</th>
                  <th className="text-center p-3 font-semibold text-[11px] uppercase tracking-[0.9px] text-[#30302F] dark:text-white w-28">Score</th>
                </tr>
              </thead>
              <tbody>
                {stories.map((story, idx) => (
                  <tr key={story.id} data-testid={`rice-row-${story.id}`} className="border-b border-[#e3e3e3]/50 dark:border-white/5 hover:bg-[#faf8fb] dark:hover:bg-white/[0.02] transition-colors">
                    <td className="p-3">
                      <span className={`text-[11px] font-bold w-5 h-5 flex items-center justify-center ${idx === 0 ? 'bg-[#98139C] text-white' : idx < 3 ? 'bg-[#f0dcf0] dark:bg-[#98139C]/20 text-[#4F0077]' : 'text-[#30302F] dark:text-white'}`}>
                        {idx + 1}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="font-medium text-[#30302F] dark:text-white">{story.title}</div>
                      {story.rice_notes && <div className="text-[11px] text-[#98139C] mt-0.5 italic">{story.rice_notes}</div>}
                    </td>
                    <td className="p-3 text-center"><EditableCell value={story.rice_reach || 0} onChange={v => updateRice(story.id, 'rice_reach', v)} min={1} max={10} /></td>
                    <td className="p-3 text-center"><EditableCell value={story.rice_impact || 0} onChange={v => updateRice(story.id, 'rice_impact', v)} min={1} max={10} /></td>
                    <td className="p-3 text-center"><EditableCell value={story.rice_confidence || 0} onChange={v => updateRice(story.id, 'rice_confidence', v)} min={1} max={10} /></td>
                    <td className="p-3 text-center"><EditableCell value={story.rice_effort || 0} onChange={v => updateRice(story.id, 'rice_effort', v)} min={1} max={10} /></td>
                    <td className="p-3">
                      <div className="text-center">
                        <span className={`text-lg font-bold ${idx === 0 ? 'text-[#98139C]' : 'text-[#30302F] dark:text-white'}`}>
                          {story.rice_score?.toLocaleString() || '—'}
                        </span>
                      </div>
                      <ScoreBar value={story.rice_score || 0} max={maxScore} color={idx === 0 ? '#98139C' : '#e3e3e3'} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bg-[#faf8fb] dark:bg-white/[0.02] border border-[#e3e3e3] dark:border-white/10 p-3 text-xs text-[#30302F] dark:text-white flex items-center gap-2">
        <TrendingUp size={14} className="text-[#98139C]" />
        <span className="font-medium text-[#30302F] dark:text-white">Traditional RICE</span> = (Reach x Impact x Confidence) / Effort — higher score = higher priority
      </div>
    </div>
  );
}
